import * as React from 'react';
import { ToTopOutlined } from '@ant-design/icons';
import * as Styles from './styled';

export interface ResizablePanelRef {
  setCollapsed: (collapsed: boolean) => void;
  toggle: () => void;
}

export interface ResizablePanelProps extends React.HTMLAttributes<HTMLDivElement> {
  defaultCollapsed?: boolean;
  left?: React.ReactNode;
  right?: React.ReactNode;
  defaultSize?: number;
  minSize?: number;
  maxSize?: number;
  lazy?: boolean;
  styles?: {
    left?: React.CSSProperties;
    right?: React.CSSProperties;
    toggleButton?: React.CSSProperties;
  };
  onResize?: (size: number) => void;
  onCollapse?: (collapsed: boolean) => void;
}

const COLLAPSED_WIDTH = 40;
const DEFAULT_SIZE = 550;
const DEFAULT_MIN_SIZE = 0;
const DEFAULT_MAX_SIZE = 800;

const LEFT_SIZE_VAR_NAME = '--left-panel-width';
const PREVIEW_OFFSET_VAR_NAME = '--preview-offset';

const getClampWidth = (size: number, min?: number, max?: number) => {
  let result = Math.max(typeof min === 'number' ? min : 0, size);

  if (typeof max === 'number') {
    result = Math.min(max, result);
  }

  return result;
};
const getClampOffset = (offset: number, size: number, min?: number, max?: number) => {
  let result = offset;

  if (typeof min === 'number') {
    result = Math.max(min - size, result);
  }

  if (typeof max === 'number') {
    result = Math.min(max - size, result);
  }

  return result;
};

const ResizablePanel = React.forwardRef<ResizablePanelRef, ResizablePanelProps>((props, ref) => {
  const {
    left,
    right,
    defaultSize = DEFAULT_SIZE,
    minSize = DEFAULT_MIN_SIZE,
    maxSize = DEFAULT_MAX_SIZE,
    defaultCollapsed,
    lazy = true,
    styles,
    style,
    onResize,
    onCollapse,
    ...p
  } = props;
  const [collapsed, setCollapsed] = React.useState(!!defaultCollapsed);
  const [isDragging, setIsDragging] = React.useState(false);
  const [size, setSize] = React.useState(() => getClampWidth(defaultSize, minSize, maxSize));
  const [offset, setOffset] = React.useState(0);
  const [transitionEnabled, setTransitionEnabled] = React.useState(true);
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const startXRef = React.useRef(0);

  // Refs to keep track of latest values in event listeners
  const minSizeRef = React.useRef(minSize);
  const maxSizeRef = React.useRef(maxSize);
  const sizeRef = React.useRef(size);
  const offsetRef = React.useRef(offset);

  React.useLayoutEffect(() => {
    minSizeRef.current = minSize;
  }, [minSize]);

  React.useLayoutEffect(() => {
    maxSizeRef.current = maxSize;
  }, [maxSize]);

  React.useLayoutEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  const handleMouseDown = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    startXRef.current = e.clientX;
    setIsDragging(true);
    setTransitionEnabled(false);
  }, []);

  const handleToggleClick = React.useCallback(() => {
    setIsTransitioning(true);
    setCollapsed((v) => !v);
  }, []);

  const handleTransitionEnd = React.useCallback<React.TransitionEventHandler<HTMLDivElement>>(
    (e) => {
      if (e.target !== e.currentTarget) return;

      requestAnimationFrame(() => {
        setIsTransitioning(false);
      });
    },
    [],
  );

  React.useEffect(() => {
    if (!isDragging) return;

    const handleMove = (e: MouseEvent) => {
      e.preventDefault();

      const delta = e.clientX - startXRef.current;

      if (lazy) {
        setOffset(getClampOffset(delta, sizeRef.current, minSizeRef.current, maxSizeRef.current));
      } else {
        requestAnimationFrame(() => {
          setSize(getClampWidth(sizeRef.current + delta, minSizeRef.current, maxSizeRef.current));
        });
      }
    };

    const handleUp = (e: MouseEvent) => {
      e.preventDefault();

      if (lazy) {
        setSize(
          getClampWidth(
            sizeRef.current + offsetRef.current,
            minSizeRef.current,
            maxSizeRef.current,
          ),
        );
        setOffset(0);
      }

      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [isDragging, lazy]);

  // Only update sizeRef when not dragging to reduce calculations during dragging
  React.useEffect(() => {
    if (!isDragging) {
      sizeRef.current = size;
      setTransitionEnabled(true);
    }
  }, [isDragging, size]);

  React.useEffect(() => {
    setSize((prev) => getClampWidth(prev, minSize, maxSize));
  }, [minSize, maxSize]);

  React.useEffect(() => {
    onCollapse?.(collapsed);
  }, [collapsed, onCollapse]);

  React.useEffect(() => {
    onResize?.(size);
  }, [size, onResize]);

  React.useImperativeHandle(
    ref,
    () => ({
      setCollapsed: (v) => {
        if (collapsed !== v) {
          setIsTransitioning(true);
        }
        setCollapsed(v);
      },
      toggle: handleToggleClick,
    }),
    [handleToggleClick, collapsed],
  );

  return (
    <Styles.Container
      {...p}
      style={
        {
          ...style,
          [LEFT_SIZE_VAR_NAME]: `${size}px`,
          [PREVIEW_OFFSET_VAR_NAME]: `${offset}px`,
        } as React.CSSProperties
      }
    >
      {left && (
        <Styles.LeftPanel
          $transitionEnabled={transitionEnabled}
          style={{ ...styles?.left, flexBasis: collapsed ? COLLAPSED_WIDTH : undefined }}
          onTransitionEnd={handleTransitionEnd}
        >
          <Styles.ToggleButton
            size="small"
            type="text"
            icon={<ToTopOutlined rotate={collapsed ? 90 : -90} />}
            style={styles?.toggleButton}
            onClick={handleToggleClick}
          />
          <Styles.LeftContainer style={{ display: collapsed ? 'none' : undefined }}>
            {left}
          </Styles.LeftContainer>
        </Styles.LeftPanel>
      )}
      {!!(left && right && !collapsed) && (
        <Styles.Dragger
          $isLazyDragging={lazy && isDragging}
          $hidden={isTransitioning}
          onMouseDown={handleMouseDown}
        />
      )}
      {right && <Styles.RightPanel style={styles?.right}>{right}</Styles.RightPanel>}
    </Styles.Container>
  );
});

ResizablePanel.displayName = 'ResizablePanel';

export default React.memo(ResizablePanel);
